/**
 * REST API 客户端工具
 *
 * 提供统一的 API 调用方法，包括文件上传等功能
 */

import { ensureAnonymousAuth, uploadFileDirect } from './cloudbase-direct-upload';
import { getMediaTypeForMedia } from './media-type';

/** 与 Web 端云盘/画布直传一致；移动端 REST 上传亦走同一云开发环境 */
const DEFAULT_CLOUDBASE_ENV_ID = 'his-1g5d6vlz6368064f';

/**
 * API 响应类型
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  code: number;
  message: string;
  data?: T;
  error?: {
    type: string;
    details?: unknown;
  };
  timestamp: string;
}

/**
 * 上传进度回调函数
 */
export type UploadProgressCallback = (progress: {
  loaded: number;
  total: number;
  percent: number;
}) => void;

/**
 * 文件上传选项
 */
export interface UploadFileOptions {
  /** 要上传的文件 */
  file: File;
  /** 文件描述（可选） */
  description?: string;
  /** 是否共享到空间（可选） */
  isSharedToSpace?: boolean;
  /** 是否共享到平台（可选） */
  isSharedToPlatform?: boolean;
  /** 分类ID列表（可选） */
  categoryIds?: string[];
  /** 上传进度回调（可选） */
  onProgress?: UploadProgressCallback;
  /**
   * 创建资产后接口返回的 `url` 策略（与后端 `createAssetDto.urlType` 一致）。
   * 需写入用户资料等长期字段时传 `permanent`；默认不传则后端多为临时签名链。
   */
  urlType?: 'temporary' | 'permanent';
}

/**
 * 文件上传结果
 */
export interface UploadFileResult {
  /** 媒体ID */
  id: string;
  /** 文件名 */
  name: string;
  /** 文件大小（字节） */
  fileSize: string | number;
  /** MIME 类型 */
  mimeType: string;
  /** 文件描述 */
  description?: string | null;
  /** 文件URL */
  url: string;
  /** 文件ID（云存储） */
  fileId?: string | null;
  /** 媒体类型 */
  type?: string | null;
  /** 创建时间 */
  createdAt?: string;
}

/**
 * API 客户端配置
 */
export interface ApiClientConfig {
  /** API 基础 URL */
  baseUrl?: string;
  /** 获取认证 token 的函数（用于移动端） */
  getToken?: () => string | null;
  /** 是否在开发环境打印日志 */
  enableDevLogs?: boolean;
  /** 云开发环境 ID（直传 COS/云存储）；默认与主站一致 */
  cloudbaseEnvId?: string;
}

/**
 * 获取 API 基础 URL
 */
function getApiBaseUrl(config?: ApiClientConfig): string {
  if (config?.baseUrl) {
    return config.baseUrl;
  }

  // ✅ 同源优先：无论开发/生产都使用相对路径
  // - 开发环境：Vite proxy 转发到本地后端
  // - 生产环境：Nginx 将 /api/* 反向代理到后端（浏览器不跨域，不受 CORS 影响）
  return '/api';
}

/**
 * 获取认证 token
 * Web 端使用 Cookie 认证，移动端使用 localStorage + Authorization header
 */
function getAuthToken(config?: ApiClientConfig): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // 检查是否是 Web 平台（通过 user agent 或平台检测）
  const isWeb = !/Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);

  if (isWeb) {
    // Web 端：使用 Cookie 认证，不需要手动设置 Authorization header
    // Cookie 会自动随请求发送
    return null;
  } else {
    // 移动端：从 localStorage 或配置的函数读取 token
    if (config?.getToken) {
      return config.getToken();
    }
    return localStorage.getItem('token');
  }
}

/**
 * 创建 API 客户端
 */
export function createApiClient(config?: ApiClientConfig) {
  const baseUrl = getApiBaseUrl(config);
  const enableDevLogs = config?.enableDevLogs ?? true;
  const cloudbaseEnvId = config?.cloudbaseEnvId ?? DEFAULT_CLOUDBASE_ENV_ID;

  function authHeaders(): HeadersInit {
    const token = getAuthToken(config);
    const h: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      (h as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
    return h;
  }

  /**
   * 直传流程：upload-auth → 云开发 SDK 上传 → POST /assets 创建资产
   */
  async function uploadViaDirectAndCreate(
    options: UploadFileOptions
  ): Promise<UploadFileResult> {
    const {
      file,
      description,
      isSharedToSpace,
      isSharedToPlatform,
      categoryIds,
      onProgress,
      urlType,
    } = options;

    const authUrl = `${baseUrl}/assets/upload-auth`;
    if (enableDevLogs) {
      console.log('[API Client] 上传鉴权:', authUrl);
    }

    const authRes = await fetch(authUrl, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        fileName: file.name,
        mimeType: file.type || undefined,
      }),
    });

    const authJson = (await authRes.json()) as ApiResponse<{ objectKey: string }>;
    if (!authJson.success || !authJson.data?.objectKey) {
      throw new Error(authJson.message || '上传鉴权失败');
    }

    await ensureAnonymousAuth(cloudbaseEnvId);
    const direct = await uploadFileDirect(
      { envId: cloudbaseEnvId },
      file,
      {
        path: authJson.data.objectKey,
        onProgress: onProgress
          ? (p) => onProgress({ loaded: p.loaded, total: p.total, percent: p.percent })
          : undefined,
      }
    );

    const createBody: Record<string, unknown> = {
      name: file.name,
      fileId: direct.fileId,
      fileSize: file.size,
      mimeType: file.type || 'application/octet-stream',
      type: getMediaTypeForMedia(file.type, file.name),
      isSharedToSpace: isSharedToSpace ?? false,
      isSharedToSystem: isSharedToPlatform ?? false,
      isOwner: false,
    };
    if (description) {
      createBody.description = description;
    }
    if (categoryIds && categoryIds.length > 0) {
      createBody.categoryId = categoryIds[0];
    }
    if (urlType != null) {
      createBody.urlType = urlType;
    }

    const createUrl = `${baseUrl}/assets`;
    if (enableDevLogs) {
      console.log('[API Client] 创建资产:', createUrl);
    }

    const createRes = await fetch(createUrl, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(createBody),
    });

    const createJson = (await createRes.json()) as ApiResponse<UploadFileResult>;
    if (!createRes.ok || !createJson.success || !createJson.data) {
      throw new Error(createJson.message || `创建资产失败 (${createRes.status})`);
    }

    return createJson.data;
  }

  /**
   * 上传文件（支持进度回调；直传云存储 + REST 落库）
   */
  async function uploadFile(options: UploadFileOptions): Promise<UploadFileResult> {
    return uploadViaDirectAndCreate(options);
  }

  /**
   * 上传文件（无独立进度回调，实现与 uploadFile 相同）
   */
  async function uploadFileSimple(
    options: Omit<UploadFileOptions, 'onProgress'>
  ): Promise<UploadFileResult> {
    return uploadViaDirectAndCreate(options);
  }

  /**
   * 带认证的文件下载（GET）：请求返回 Blob，触发浏览器下载
   * 用于导出等场景，与 REST 下载接口配合（Cookie 或 Authorization 认证）
   *
   * @param path 路径（不含 baseUrl），如 'activity_registrations/export/sign-off'
   * @param queryParams 查询参数，会拼到 URL
   * @param filename 下载保存的文件名（可选，默认 'download'）
   */
  async function downloadFile(
    path: string,
    queryParams?: Record<string, string>,
    filename?: string
  ): Promise<void> {
    const pathPrefix = path.startsWith('/') ? '' : '/';
    const queryString =
      queryParams && Object.keys(queryParams).length > 0
        ? '?' +
          Object.entries(queryParams)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        : '';
    const url = `${baseUrl}${pathPrefix}${path}${queryString}`;

    const token = getAuthToken(config);
    const headers: HeadersInit = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      let errorMessage = `下载失败 (${response.status})`;
      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        try {
          const error: ApiResponse = await response.json();
          errorMessage = error.message || error.error?.type || errorMessage;
        } catch {
          // ignore
        }
      }
      throw new Error(errorMessage);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition');
    const fromDisposition =
      disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i)?.[1]?.trim() ||
      disposition?.match(/filename="?([^";\n]+)"?/i)?.[1]?.trim();
    const resolvedFilename = filename ?? fromDisposition ?? 'download';

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = decodeURIComponent(resolvedFilename);
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  return {
    uploadFile,
    uploadFileSimple,
    downloadFile,
  };
}

/**
 * 默认 API 客户端实例
 */
export const apiClient = createApiClient();

